"use client";

import { useLazyQuery } from "@apollo/client";
import { Check, Zap } from "lucide-react";
import { HAS_USER_OPENED_QUICKIES } from "@constants/global";
import { useChatStore } from "stores/chat.store";
import useTranslation from "next-translate/useTranslation";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import toast from "react-hot-toast";
import Lottie from "react-lottie";
import {
  Checkbox,
  Input,
  LiquidPill,
  Modal,
  ModalButtons,
  PageEmptyStateV2,
  PageLoadingStateV2,
} from "components/ui/v2";
import { cn } from "@/lib/utils";
import { forceRefetchQueries } from "settings/apollo";
import useSound from "use-sound";
import { Z_INDEX } from "components/ui/v2/z-index";
import lottieSuccess from "utils/lottie/success.json";
import thinkingAi from "utils/lottie/thinking.json";
import { gqlUser, gqlVendor } from "../../gql";
import {
  IQuickiesInput,
  IQuickiesRes,
  VendorQuickie,
} from "../../gql/Vendor/queries";
import { IQuickieList } from "./quickie.context";
import { ISelectedQuickieItems } from "./quickie.provider";
import { insertQuickies } from "./quickie.utils";

type TThunderAnim = "" | "vibrating";

interface IQuickie {
  isBubbleVisible: boolean;
  suppressDefaultBubble: boolean;
  list: IQuickieList | undefined;
  setOpenQuickie: Dispatch<SetStateAction<() => void>>;
  setCloseQuickie: Dispatch<SetStateAction<() => void>>;
  setIsBubbleVisible: (isBubbleVisible: boolean) => void;
}

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: lottieSuccess,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const defaultThinkingOptions = {
  loop: true,
  autoplay: true,
  animationData: thinkingAi,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const Quickie = (props: IQuickie) => {
  const { t } = useTranslation("common");
  const { isChatListOpen } = useChatStore();
  const [items, setItems] = useState<VendorQuickie[]>([]);
  const [search, setSearch] = useState<string>("");

  const [playComplete] = useSound("/sounds/feedback-sound.mp3", {
    volume: 0.25,
  });

  const {
    isBubbleVisible,
    suppressDefaultBubble,
    list,
    setOpenQuickie,
    setCloseQuickie,
    setIsBubbleVisible,
  } = props;

  const [getQuickies, { loading: quickiesLoading, data }] = useLazyQuery<
    IQuickiesRes,
    IQuickiesInput
  >(gqlVendor.queries.GET_QUICKIES, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ quickies }) => {
      setItems(quickies || []);
      if (!quickies.length) {
        setIsBubbleVisible(false);
      }
    },
  });

  const [showWelcome, setShowWelcome] = useState<boolean>(false);
  const [thunderAnim, setThunderAnim] = useState<TThunderAnim>("");
  const [isListVisible, setIsListVisible] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<ISelectedQuickieItems>({});
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isCreated, setIsCreated] = useState<boolean>(false);

  useEffect(() => {
    if (!localStorage.getItem(HAS_USER_OPENED_QUICKIES)) {
      setThunderAnim("vibrating");
    }

    setOpenQuickie(() => showList);
    setCloseQuickie(() => hideList);
  }, []);

  useEffect(() => {
    if (!list?.type) return;

    setItems([]);
    setIsListVisible(false);
    getQuickies({
      variables: {
        getQuickiesInput: {
          type: list.type,
        },
      },
    });
  }, [list]);

  const showList = () => {
    if (!localStorage.getItem(HAS_USER_OPENED_QUICKIES)) {
      setShowWelcome(true);
    }
    setIsListVisible(true);
  };

  const hideList = () => {
    setSearch("");
    setIsListVisible(false);
  };

  const onWelcomeClose = () => {
    setShowWelcome(false);
  };

  const onClose = () => {
    setSearch("");
    setIsListVisible(false);
  };

  const onSelectItem = (item: string) => {
    const newSelectedItems = list?.isSingleChoice ? {} : { ...selectedItems };

    if (selectedItems[item]) {
      delete newSelectedItems[item];
    } else {
      newSelectedItems[item] = item;
    }

    setSelectedItems(newSelectedItems);
  };

  const handleCreate = async () => {
    if (!list?.type) {
      toast.error(t("ERROR_CREATING_QUICKIES_NO_TYPE"));
      return;
    }

    try {
      setIsCreating(true);
      await insertQuickies(list.type, Object.keys(selectedItems));
      setIsCreated(true);
      await forceRefetchQueries([
        ...(list?.refetchQueries || []),
        gqlVendor.queries.GET_QUICKIES,
        gqlUser.queries.USER_ONBOARDING,
      ]);

      localStorage.setItem(HAS_USER_OPENED_QUICKIES, "true");

      playComplete();

      setTimeout(() => {
        setSelectedItems({});
        setIsCreated(false);
        setIsCreating(false);
      }, 4000);
    } catch (e) {
      const error = e as Error;
      const message = error.message || t("ERROR_CREATING_QUICKIES");
      toast.error(message);
      setIsCreating(false);
    }
  };

  const handleSearch = (text: string) => {
    setSearch(text);

    if (!text) {
      setItems(data?.quickies || []);
      return;
    }

    const filteredItems = data?.quickies.filter((item) =>
      item.name.toLowerCase().includes(text.toLowerCase())
    );

    setItems(filteredItems || []);
  };

  const hasPageOwnedQuickieTrigger =
    list?.type === "career" ||
    list?.type === "item_path" ||
    list?.type === "appreciation";

  const selectedCount = Object.keys(selectedItems).length;
  const showSearch = (data?.quickies?.length || 0) > 10;

  const modalFooter = showWelcome ? (
    <div className="flex w-full justify-end pt-0">
      <LiquidPill
        as="button"
        type="button"
        variant="indigo"
        appearance="tinted"
        surfaceStyle="selectionSolid"
        size="md"
        onClick={onWelcomeClose}
      >
        {t("¡Entendido!")}
      </LiquidPill>
    </div>
  ) : isCreating ? null : (
    <ModalButtons
      compact
      className="!pt-0"
      loading={quickiesLoading || isCreating}
      disabled={quickiesLoading || selectedCount === 0}
      onCancel={onClose}
      onSubmit={handleCreate}
      submitLabel={t("Crear ahora")}
    />
  );

  return (
    <>
      {isBubbleVisible &&
        !suppressDefaultBubble &&
        !hasPageOwnedQuickieTrigger &&
        !isChatListOpen && (
          <div
            id="quickie-bubble"
            className="fixed bottom-5 right-5 flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-white shadow-md dark:bg-gray-800 md:bottom-[4.5rem]"
            onClick={showList}
            style={{
              zIndex: Z_INDEX.FIXED_CIRCLE,
            }}
          >
            <span role="img" className={`text-3xl ${thunderAnim}`}>
              ⚡
            </span>
          </div>
        )}

      <Modal
        open={isListVisible}
        onClose={onClose}
        title={
          showWelcome
            ? t("Conoce los quickies")
            : list?.header || t("Evaluaciones")
        }
        description={
          showWelcome
            ? undefined
            : list?.description ||
              t("Crea evaluaciones para tu organización de forma rápida")
        }
        eyebrow={showWelcome ? undefined : t("Quickie")}
        size="md"
        bodyScrollFade
        closeButtonLabel={t("Cerrar")}
        panelClassName="flex max-h-[min(88vh,680px)] flex-col"
        headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
        bodyClassName="px-4 py-3 sm:px-5 sm:py-3.5"
        footer={modalFooter}
      >
        {showWelcome ? (
          <div className="space-y-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-300">
              <Zap className="h-5 w-5" aria-hidden />
            </div>
            <p className="text-[13px] leading-relaxed text-slate-600 dark:text-zinc-300">
              {t("Genera tus elementos de la manera")}{" "}
              <span className="font-semibold text-slate-900 dark:text-zinc-50">
                {t("más rápida")}
              </span>{" "}
              {t("a partir de plantillas que tenemos diseñadas para ti")}
            </p>
            <p className="text-[13px] leading-relaxed text-slate-600 dark:text-zinc-300">
              {t(
                "Solo debes elegir de la lista lo que necesitas y nosotros lo creamos a la "
              )}
              <span className="italic text-slate-900 dark:text-zinc-50">
                {t("velocidad de la luz")}
              </span>
            </p>
          </div>
        ) : isCreating ? (
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 px-4 text-center">
            <Lottie
              options={isCreated ? defaultOptions : defaultThinkingOptions}
              height={140}
              width={140}
            />
            <p className="text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
              {isCreated ? t("GOOD_NEWS") : t("WAIT_A_MOMENT")}
            </p>
            <p className="text-[13px] font-medium leading-snug text-slate-900 dark:text-zinc-50">
              {isCreated ? t("UPDATING_INFO") : t("CREATING_ITEMS")}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {showSearch ? (
              <Input
                size="compact"
                variant="violet"
                value={search}
                onChange={(event) => handleSearch(event.target.value)}
                placeholder={`${t("Buscar")} ${list?.header?.toLowerCase() || ""}`}
                aria-label={t("Buscar")}
              />
            ) : null}

            {quickiesLoading && items.length === 0 ? (
              <PageLoadingStateV2
                embedded
                title={t("Cargando")}
                description={t("Un momento, por favor")}
              />
            ) : items.length === 0 ? (
              <PageEmptyStateV2
                embedded
                variant="noResult"
                title={t("No se encontraron resultados")}
                description={t("Prueba con otro término de búsqueda")}
              />
            ) : (
              <div
                className="flex flex-col gap-1"
                role="listbox"
                aria-multiselectable={!list?.isSingleChoice}
              >
                {items.map((item) => {
                  const selected = Boolean(selectedItems[item.id]);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => onSelectItem(item.id)}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-lg px-1 py-2 text-left transition-colors sm:px-0.5",
                        selected
                          ? "bg-indigo-50/80 dark:bg-indigo-950/40"
                          : "hover:bg-slate-50/90 dark:hover:bg-zinc-900/60"
                      )}
                    >
                      {list?.isSingleChoice ? (
                        <span
                          className={cn(
                            "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2",
                            selected
                              ? "border-indigo-500 bg-indigo-500 text-white"
                              : "border-slate-300 dark:border-zinc-600"
                          )}
                          aria-hidden
                        >
                          {selected ? (
                            <Check className="h-2.5 w-2.5" strokeWidth={3} />
                          ) : null}
                        </span>
                      ) : (
                        <Checkbox
                          checked={selected}
                          tabIndex={-1}
                          className="pointer-events-none mt-0"
                          aria-hidden
                        />
                      )}
                      <span className="min-w-0 flex-1 text-[13px] font-medium leading-snug text-slate-900 dark:text-zinc-50">
                        {item.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default Quickie;
